"use client";

import { useClient } from "@urql/next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAccumulateBatch } from "./use-accumulate-batch";
const INACTIVITY_TIMEOUT = 1000;

/**
 * We wrap our own useSubscription as the built in urql one keeps track of its own internal state,
 * thus causing a re-render for every update for the Component that uses it.
 * We only really care about the data updates, so we can just use the raw subscription callback.
 */
export const useSubscription = ({
  query,
  variables,
  pause,
  context
}, {
  onResult,
  onError,
  onUnsubscribe,
  pauseOnInactivity = true,
  enableBatching = typeof window !== "undefined" && process.env.NODE_ENV !== "test",
  batchDelay = 2000,
  batchLeading = true,
  resetKey
}) => {
  const client = useClient();
  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  const batchCallback = useCallback(results => {
    results.forEach(result => onResultRef.current(result));
  }, []);
  const variablesKey = JSON.stringify(variables);
  const accumulateBatch = useAccumulateBatch(batchCallback, {
    delay: batchDelay,
    leading: batchLeading,
    resetKey: resetKey || variablesKey
  });
  const memoedVariables = useMemo(() => variables, [variablesKey]);

  // State to track if document has been hidden for more than 5 seconds
  const [isPausedByVisibility, setIsPausedByVisibility] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !pauseOnInactivity) {
      return;
    }
    let timer;
    function handleVisibilityChange() {
      if (window.document.visibilityState === "hidden") {
        timer = setTimeout(() => {
          setIsPausedByVisibility(true);
        }, INACTIVITY_TIMEOUT);
      } else {
        clearTimeout(timer);
        setIsPausedByVisibility(false);
      }
    }
    window.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("visibilitychange", handleVisibilityChange);
      clearTimeout(timer);
    };
  }, [pauseOnInactivity]);
  useEffect(() => {
    // If either the pause prop is true or the document has been hidden for more than 5 seconds, do not subscribe.
    if (pause || isPausedByVisibility) {
      return;
    }
    const subscription = client.subscription(query, memoedVariables, context).subscribe(({
      data,
      error
    }) => {
      if (data) {
        if (enableBatching) {
          accumulateBatch(data);
        } else {
          onResultRef.current(data);
        }
      }
      if (error) {
        onErrorRef.current?.(error);
      }
    });
    return () => {
      subscription.unsubscribe();
      onUnsubscribe?.();
    };
  }, [query, memoedVariables, pause, isPausedByVisibility, client, context, onUnsubscribe, enableBatching, batchDelay, batchLeading, resetKey]);
};